import { z } from "zod";
// ─────────────────────────────────────────────────────────────────────────────
// Piste V10 — Phase 5 : Sell — la mini-boutique (EPIC 16, US-161→16E).
// Schémas partagés client/serveur pour la boutique du pro, le catalogue produits
// et les commandes.
//
// GATE (AGENTS.md §10) : le socle est INERTE côté paiement tant que
// MONETIZATION_ENABLED est false — aucune commande n'est encaissée, aucun appel
// Stripe. Ces schémas décrivent la frontière ; c'est la route qui refuse (403)
// quand le flag est OFF. `CreateOrderInput` prépare une commande, il ne débite
// jamais par lui-même.
//
// RÈGLE US-125 / US-16A (cloison données perso) : côté vendeur, `OrderDTO`
// n'expose QUE l'adresse de livraison et l'email fournis par l'acheteur pour la
// commande — jamais d'autres données personnelles du compte acheteur.
//
// CONVENTIONS : montants en centimes (Int, TTC) ; TVA en MILLIÈMES
// (`vatRatePermille` : 200/100/55/21 ‰ = 20/10/5,5/2,1 %) pour éviter les
// décimales. Les lignes de commande figent des SNAPSHOTS (libellé/prix/SKU) au
// moment de l'achat.
// ─────────────────────────────────────────────────────────────────────────────
// ── Enums (miroir des enums Prisma) ──────────────────────────────────────────
export const VatRegimeSchema = z.enum(["FRANCHISE", "ASSUJETTI"]);
export const OrderStatusSchema = z.enum([
    "PENDING",
    "PAID",
    "SHIPPED",
    "DELIVERED",
    "REFUNDED",
    "CANCELLED",
]);
// ── Briques communes ─────────────────────────────────────────────────────────
/** Code pays ISO-3166-1 alpha-2 (US-166 : origine & lieu d'expédition). */
export const Iso2CountrySchema = z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/, "country must be an ISO-3166-1 alpha-2 code");
/**
 * Taux de TVA autorisés en France, exprimés en MILLIÈMES (US-162) :
 * 200 ‰ = 20 %, 100 ‰ = 10 %, 55 ‰ = 5,5 %, 21 ‰ = 2,1 %.
 * `null` = franchise en base (art. 293 B du CGI) : aucune TVA.
 */
export const VatRatePermilleSchema = z
    .union([z.literal(200), z.literal(100), z.literal(55), z.literal(21)])
    .nullable();
/**
 * Adresse de livraison fournie par l'acheteur pour une commande (US-169). C'est
 * la SEULE donnée d'adresse que le vendeur reçoit (nécessaire à l'envoi) —
 * cloison US-125 : rien de plus que ce que l'acheteur saisit ici.
 */
export const ShippingAddressSchema = z.object({
    fullName: z.string().trim().min(1).max(200),
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).optional(),
    postalCode: z.string().trim().min(1).max(20),
    city: z.string().trim().min(1).max(120),
    country: Iso2CountrySchema.default("FR"),
});
// ── Boutique (US-161/162) ────────────────────────────────────────────────────
/** État public/pro de la boutique. */
export const ShopSettingsDTOSchema = z.object({
    businessId: z.string().uuid(),
    active: z.boolean(),
    vatRegime: VatRegimeSchema,
    shippingProvider: z.string().nullable(),
    createdAt: z.string().datetime(),
});
/**
 * Réglages modifiables de la boutique (US-161/162/164). Tous optionnels : mise à
 * jour partielle. L'activation en un tap = `{ active: true }` sans autre config.
 */
export const UpdateShopSettingsInputSchema = z
    .object({
    active: z.boolean().optional(),
    vatRegime: VatRegimeSchema.optional(),
    shippingProvider: z.string().trim().max(120).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: "at least one field must be provided",
});
// ── Produit (US-165/166/167/168) ─────────────────────────────────────────────
/**
 * DTO produit public (fiche + listing boutique). Tous les champs sont
 * consommables par l'acheteur : les mentions légales obligatoires US-166
 * (origine, expédition, délai, frais, SKU) DOIVENT être affichées. Aucun champ
 * interne (marge, stats privées, plan) n'est exposé.
 */
export const ProductDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    title: z.string(),
    description: z.string(),
    /** Clés d'upload des images (1re = principale). */
    images: z.array(z.string()),
    priceCents: z.number().int().positive(),
    currency: z.string(),
    vatRatePermille: VatRatePermilleSchema,
    sku: z.string(),
    originCountry: Iso2CountrySchema,
    shipFromCountry: Iso2CountrySchema,
    shipDelayDays: z.number().int().nonnegative(),
    shippingFeeCents: z.number().int().nonnegative(),
    stock: z.number().int().nonnegative(),
    discountCents: z.number().int().positive().nullable(),
    discountPercent: z.number().int().min(1).max(100).nullable(),
    discountStart: z.string().datetime().nullable(),
    discountEnd: z.string().datetime().nullable(),
    /** US-167 : prix de référence « omnibus » (plus bas des 30 derniers jours). */
    refPrice30dCents: z.number().int().nonnegative().nullable(),
    active: z.boolean(),
    createdAt: z.string().datetime(),
});
/**
 * Cohérence d'une réduction (US-167) : montant OU pourcentage (pas les deux), et
 * si une période est fournie, `discountStart` < `discountEnd`. Réutilisé par la
 * création et la mise à jour.
 */
const discountRefinement = (v, ctx) => {
    if (v.discountCents != null && v.discountPercent != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "discountCents and discountPercent are mutually exclusive",
            path: ["discountPercent"],
        });
    }
    if (v.discountStart != null &&
        v.discountEnd != null &&
        new Date(v.discountEnd).getTime() <= new Date(v.discountStart).getTime()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "discountEnd must be after discountStart",
            path: ["discountEnd"],
        });
    }
};
/**
 * Création d'un produit (US-165/166). Valide les MENTIONS OBLIGATOIRES US-166 :
 * 1..7 images, titre, prix TTC > 0, SKU non vide, origine & lieu d'expédition
 * ISO-2, délai & frais de port ≥ 0. `vatRatePermille` doit valoir null
 * (franchise) ou l'un de 200/100/55/21. La publication effective (`active`) est
 * gardée côté route (bloquée tant qu'un champ obligatoire manque).
 */
export const CreateProductInputSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(5000),
    images: z.array(z.string().trim().min(1)).min(1).max(7),
    priceCents: z.number().int().positive(),
    currency: z.string().trim().length(3).toLowerCase().default("eur"),
    vatRatePermille: VatRatePermilleSchema.default(null),
    sku: z.string().trim().min(1).max(100),
    originCountry: Iso2CountrySchema,
    shipFromCountry: Iso2CountrySchema,
    shipDelayDays: z.number().int().nonnegative().max(365).default(3),
    shippingFeeCents: z.number().int().nonnegative().default(0),
    stock: z.number().int().nonnegative().default(0),
    discountCents: z.number().int().positive().nullable().default(null),
    discountPercent: z.number().int().min(1).max(100).nullable().default(null),
    discountStart: z.string().datetime().nullable().default(null),
    discountEnd: z.string().datetime().nullable().default(null),
    active: z.boolean().default(false),
})
    .superRefine(discountRefinement);
/**
 * Mise à jour partielle d'un produit. Mêmes règles de cohérence de réduction que
 * la création.
 */
export const UpdateProductInputSchema = z
    .object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().min(1).max(5000).optional(),
    images: z.array(z.string().trim().min(1)).min(1).max(7).optional(),
    priceCents: z.number().int().positive().optional(),
    vatRatePermille: VatRatePermilleSchema.optional(),
    sku: z.string().trim().min(1).max(100).optional(),
    originCountry: Iso2CountrySchema.optional(),
    shipFromCountry: Iso2CountrySchema.optional(),
    shipDelayDays: z.number().int().nonnegative().max(365).optional(),
    shippingFeeCents: z.number().int().nonnegative().optional(),
    stock: z.number().int().nonnegative().optional(),
    discountCents: z.number().int().positive().nullable().optional(),
    discountPercent: z.number().int().min(1).max(100).nullable().optional(),
    discountStart: z.string().datetime().nullable().optional(),
    discountEnd: z.string().datetime().nullable().optional(),
    active: z.boolean().optional(),
})
    .superRefine(discountRefinement)
    .refine((v) => Object.keys(v).length > 0, {
    message: "at least one field must be provided",
});
// ── Commande (US-169/16A/16B/16C) ────────────────────────────────────────────
/**
 * Ligne de commande — snapshots figés à l'achat (US-16A). Le libellé, le SKU, le
 * prix unitaire et le taux de TVA restent ceux du moment de la commande, même si
 * le produit change ou disparaît (`productId` peut être null).
 */
export const OrderItemDTOSchema = z.object({
    id: z.string().uuid(),
    productId: z.string().uuid().nullable(),
    titleSnapshot: z.string(),
    skuSnapshot: z.string(),
    quantity: z.number().int().positive(),
    unitPriceCents: z.number().int().nonnegative(),
    vatRatePermille: VatRatePermilleSchema,
});
/**
 * DTO commande — vue acheteur ET vendeur. Le vendeur voit `shippingAddress` et
 * `buyerEmail` (nécessaires à l'envoi/facture) mais AUCUNE autre donnée perso du
 * compte acheteur (cloison US-125/16A). Les identifiants Stripe internes ne sont
 * jamais exposés.
 */
export const OrderDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    buyerId: z.string().uuid().nullable(),
    buyerEmail: z.string().email(),
    status: OrderStatusSchema,
    subtotalCents: z.number().int().nonnegative(),
    vatCents: z.number().int().nonnegative(),
    shippingCents: z.number().int().nonnegative(),
    totalCents: z.number().int().nonnegative(),
    shippingAddress: ShippingAddressSchema,
    invoiceNumber: z.string().nullable(),
    trackingNumber: z.string().nullable(),
    trackingUrl: z.string().nullable(),
    items: z.array(OrderItemDTOSchema),
    createdAt: z.string().datetime(),
    paidAt: z.string().datetime().nullable(),
    shippedAt: z.string().datetime().nullable(),
    deliveredAt: z.string().datetime().nullable(),
});
/**
 * Préparation d'une commande (US-169). Le client fournit les lignes (produit +
 * quantité), l'adresse de livraison et l'email de contact. Les prix, la TVA et
 * les totaux sont TOUJOURS recalculés côté serveur depuis le catalogue (jamais
 * de montant transmis par le client). GATÉ : l'encaissement est refusé tant que
 * MONETIZATION_ENABLED est false.
 */
export const CreateOrderInputSchema = z.object({
    businessId: z.string().uuid(),
    items: z
        .array(z.object({
        productId: z.string().uuid(),
        quantity: z.number().int().positive().max(999),
    }))
        .min(1)
        .max(50),
    shippingAddress: ShippingAddressSchema,
    buyerEmail: z.string().trim().email(),
});
/**
 * TVA contenue dans un montant TTC, taux en millièmes. Les prix affichés étant
 * TTC (US-162), on EXTRAIT la TVA : TVA = TTC × rate / (1000 + rate). Retourne 0
 * en franchise (rate null ou 0). Arrondi au centime le plus proche.
 *
 * Ex. 12,00 € TTC à 20 % (200 ‰) → 1200 × 200 / 1200 = 200 ⇒ 2,00 € de TVA.
 */
export function vatFromTtcCents(ttcCents, vatRatePermille) {
    if (vatRatePermille == null || vatRatePermille === 0)
        return 0;
    return Math.round((ttcCents * vatRatePermille) / (1000 + vatRatePermille));
}
/**
 * Calcule les totaux d'une commande à partir des lignes (TTC) et des frais de
 * port. Fonction PURE et réutilisable (checkout, facture, tests). Le port n'est
 * pas assujetti à une TVA distincte ici (simplification MVP : à affiner si le
 * régime l'exige).
 */
export function computeOrderTotals(lines, shippingCents = 0) {
    let subtotalCents = 0;
    let vatCents = 0;
    for (const line of lines) {
        const lineTtc = line.unitPriceCents * line.quantity;
        subtotalCents += lineTtc;
        vatCents += vatFromTtcCents(lineTtc, line.vatRatePermille);
    }
    return {
        subtotalCents,
        vatCents,
        shippingCents,
        totalCents: subtotalCents + shippingCents,
    };
}
//# sourceMappingURL=pro4.js.map